import { InfoCircleFilled, SearchOutlined } from '@ant-design/icons';
import { Checkbox, Input, Popover, Typography } from 'antd';
import cn from 'classnames';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from 'react';

import type { NSocialIntegrations } from '@app/services/rtkQuery/SocialIntegrations/types';

import '@pages/Integrations/IntegrationsList/styles/BeelineAbonentsSelector.desktop.scss';

const { Text } = Typography;
const PF = 'BeelineAbonentsSelector';

type Abonent = NSocialIntegrations.ModelAtcAbonent;

interface BeelineAbonentsSelectorProps {
  abonents: Abonent[] | null | undefined;
  activeBranchName: string | null;
  disabled?: boolean;
  onSelectedChange?: (atcIds: string[]) => void;
}

const POPOVER_OVERLAY_CLASS = `${PF}__popoverOverlay`;

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const normalizePhone = (value: string) => value.replace(/\D/g, '');

const getAtcId = (abonent: Abonent): string | null =>
  isNonEmptyString(abonent.AtcId) ? abonent.AtcId : null;

const isSelectableByBusinessRules = (abonent: Abonent): boolean =>
  Boolean(abonent.IsAvailable || abonent.IsSelected);

const hasAtcUserInfo = (abonent: Abonent): boolean =>
  Boolean(
    abonent.AtcUserName || abonent.AtcUserEmail || abonent.AtcUserDepartment
  );

interface PopoverProps {
  abonent: Abonent;
}

const PopoverWarningContent: FC = memo(() => (
  <div className={`${PF}__popover`}>
    <div className={`${PF}__popover-line`}>
      <InfoCircleFilled
        className={cn(`${PF}__info-icon`, `${PF}__info-icon--warning`)}
      />
      <Text>
        Запись разговоров отключена для абонента в личном кабинете АТС Билайн.
      </Text>
    </div>
  </div>
));
PopoverWarningContent.displayName = 'PopoverWarningContent';

const PopoverInfoATSContent: FC<PopoverProps> = memo(({ abonent }) => {
  const { AtcUserName, AtcUserEmail, AtcUserDepartment } = abonent;

  return (
    <div className={`${PF}__popover`}>
      <div className={`${PF}__popover-title`}>Информация из АТС Билайн:</div>

      <div className={`${PF}__popover-grid`}>
        {!!AtcUserName && (
          <div className={`${PF}__popover-field`}>
            <span className={`${PF}__popover-field-label`}>Имя:</span>
            <span>{AtcUserName}</span>
          </div>
        )}

        {!!AtcUserEmail && (
          <div className={`${PF}__popover-field`}>
            <span className={`${PF}__popover-field-label`}>Email:</span>
            <span>{AtcUserEmail}</span>
          </div>
        )}

        {!!AtcUserDepartment && (
          <div className={`${PF}__popover-field`}>
            <span className={`${PF}__popover-field-label`}>Отдел:</span>
            <span>{AtcUserDepartment}</span>
          </div>
        )}
      </div>
    </div>
  );
});
PopoverInfoATSContent.displayName = 'PopoverInfoATSContent';

const PopoverUsedAtContent: FC<PopoverProps> = memo(({ abonent }) => (
  <div className={`${PF}__popover`}>
    <div className={`${PF}__popover-grid`}>
      <div className={`${PF}__popover-field`}>
        <span className={`${PF}__popover-field-label`}>Филиал:</span>
        <span>{abonent.UsedAtBranch || '—'}</span>
      </div>

      <div className={`${PF}__popover-field`}>
        <span className={`${PF}__popover-field-label`}>Интеграция:</span>
        <span>{abonent.UsedAtIntegration || '—'}</span>
      </div>

      <div className={`${PF}__popover-line`}>
        <InfoCircleFilled
          className={cn(`${PF}__info-icon`, `${PF}__info-icon--error`)}
        />
        <Text>
          Нельзя подключить телефон абонента одновременно в нескольких филиалах
          (интеграциях).
        </Text>
      </div>
    </div>
  </div>
));
PopoverUsedAtContent.displayName = 'PopoverUsedAtContent';

const IconSlot: FC<{ children?: React.ReactNode }> = ({ children }) => (
  <span className={`${PF}__icon-slot`}>{children}</span>
);

interface NumberRowProps {
  abonent: Abonent;
  checked: boolean;
  disabled: boolean;
  onChange: (nextChecked: boolean) => void;
  showRecordingWarning: boolean;
  showUsedAtInfo: boolean;
}

const NumberRow: FC<NumberRowProps> = memo(
  ({
    abonent,
    checked,
    disabled,
    onChange,
    showRecordingWarning,
    showUsedAtInfo,
  }) => {
    const phoneNumber = abonent.PhoneNumber ?? '';
    const isPhoneValid = isNonEmptyString(phoneNumber);

    const phoneNode = useMemo(() => {
      const phoneText = (
        <span className={`${PF}__phone-text`}>
          {isPhoneValid ? phoneNumber : 'Номер не указан'}
        </span>
      );

      if (!isPhoneValid || !hasAtcUserInfo(abonent)) return phoneText;

      return (
        <Popover
          classNames={{ root: POPOVER_OVERLAY_CLASS }}
          content={<PopoverInfoATSContent abonent={abonent} />}
          trigger="hover"
        >
          {phoneText}
        </Popover>
      );
    }, [abonent, isPhoneValid, phoneNumber]);

    return (
      <div className={`${PF}__row`}>
        <Checkbox
          className={`${PF}__checkbox-wrapper`}
          checked={checked}
          disabled={disabled || !isPhoneValid}
          onChange={(e) => onChange(e.target.checked)}
        >
          {phoneNode}
        </Checkbox>

        {showRecordingWarning ? (
          <IconSlot>
            <Popover
              classNames={{ root: POPOVER_OVERLAY_CLASS }}
              content={<PopoverWarningContent />}
              trigger="hover"
            >
              <InfoCircleFilled
                className={cn(`${PF}__info-icon`, `${PF}__info-icon--warning`)}
              />
            </Popover>
          </IconSlot>
        ) : (
          <IconSlot />
        )}

        {showUsedAtInfo ? (
          <IconSlot>
            <Popover
              classNames={{ root: POPOVER_OVERLAY_CLASS }}
              content={<PopoverUsedAtContent abonent={abonent} />}
              trigger="hover"
            >
              <InfoCircleFilled
                className={cn(`${PF}__info-icon`, `${PF}__info-icon--info`)}
              />
            </Popover>
          </IconSlot>
        ) : (
          <IconSlot />
        )}
      </div>
    );
  }
);
NumberRow.displayName = 'NumberRow';

export const BeelineAbonentsSelector: FC<BeelineAbonentsSelectorProps> = ({
  abonents,
  activeBranchName,
  disabled,
  onSelectedChange,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAtcIds, setSelectedAtcIds] = useState<string[]>([]);
  const isInitializedRef = useRef(false);

  const safeAbonents = useMemo(() => abonents ?? [], [abonents]);

  const normalizedSearch = useMemo(
    () => normalizePhone(searchTerm),
    [searchTerm]
  );

  const filteredAbonents = useMemo(() => {
    if (!normalizedSearch) return safeAbonents;

    return safeAbonents.filter(({ PhoneNumber }) => {
      const phone = isNonEmptyString(PhoneNumber)
        ? normalizePhone(PhoneNumber)
        : '';
      return phone.includes(normalizedSearch);
    });
  }, [safeAbonents, normalizedSearch]);

  const availableAbonents = useMemo(
    () =>
      filteredAbonents.filter((abonent) => {
        if (!isSelectableByBusinessRules(abonent)) return false;
        return getAtcId(abonent) !== null;
      }),
    [filteredAbonents]
  );

  const unavailableAbonents = useMemo(
    () =>
      filteredAbonents.filter((abonent) => {
        if (!isSelectableByBusinessRules(abonent)) return true;

        return getAtcId(abonent) === null;
      }),
    [filteredAbonents]
  );

  const selectableAtcIdsInView = useMemo(
    () =>
      availableAbonents
        .map(getAtcId)
        .filter((id): id is string => isNonEmptyString(id)),
    [availableAbonents]
  );

  const selectedSet = useMemo(() => new Set(selectedAtcIds), [selectedAtcIds]);

  const selectedInViewCount = useMemo(() => {
    let count = 0;
    for (const id of selectableAtcIdsInView) {
      if (selectedSet.has(id)) count += 1;
    }
    return count;
  }, [selectableAtcIdsInView, selectedSet]);

  const allChecked =
    selectableAtcIdsInView.length > 0 &&
    selectedInViewCount === selectableAtcIdsInView.length;

  const indeterminate = selectedInViewCount > 0 && !allChecked;

  useEffect(() => {
    if (safeAbonents.length === 0) return;

    const selectableIds = new Set(
      safeAbonents
        .filter(isSelectableByBusinessRules)
        .map(getAtcId)
        .filter((id): id is string => isNonEmptyString(id))
    );

    setSelectedAtcIds((prev) => {
      if (!isInitializedRef.current) {
        isInitializedRef.current = true;

        const initiallySelected = safeAbonents
          .filter(({ IsSelected }) => IsSelected)
          .map(getAtcId)
          .filter((id): id is string => isNonEmptyString(id));

        const base = initiallySelected.length > 0 ? initiallySelected : prev;
        return base.filter((id) => selectableIds.has(id));
      }

      return prev.filter((id) => selectableIds.has(id));
    });
  }, [safeAbonents]);

  useEffect(() => {
    onSelectedChange?.(selectedAtcIds);
  }, [selectedAtcIds, onSelectedChange]);

  const setAtcIdChecked = useCallback(
    (atcId: string, nextChecked: boolean) => {
      if (disabled) return;

      setSelectedAtcIds((prev) => {
        const alreadySelected = prev.includes(atcId);
        if (nextChecked) return alreadySelected ? prev : [...prev, atcId];
        return alreadySelected ? prev.filter((id) => id !== atcId) : prev;
      });
    },
    [disabled]
  );

  const onToggleAll = useCallback(() => {
    if (disabled) return;

    setSelectedAtcIds((prev) => {
      const next = new Set(prev);

      if (allChecked) {
        for (const id of selectableAtcIdsInView) next.delete(id);
      } else {
        for (const id of selectableAtcIdsInView) next.add(id);
      }

      return Array.from(next);
    });
  }, [allChecked, selectableAtcIdsInView, disabled]);

  return (
    <div className={PF}>
      <Text strong className="label label-require">
        Выберите телефонные номера, которые будут интегрированы с филиалом{' '}
        {activeBranchName}
      </Text>

      <div className={`${PF}__header`}>
        <Checkbox
          className={`${PF}__checkbox-select-all`}
          disabled={disabled || selectableAtcIdsInView.length === 0}
          indeterminate={indeterminate}
          checked={allChecked}
          onChange={onToggleAll}
        >
          Выбрать все
        </Checkbox>

        <Input
          className={`${PF}__search`}
          placeholder="Поиск номера"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          allowClear
          suffix={<SearchOutlined />}
          disabled={disabled}
        />
      </div>

      <div className={`${PF}__list`}>
        {availableAbonents.map((abonent, index) => {
          const atcId = getAtcId(abonent);
          const phone = abonent.PhoneNumber ?? '';
          const isPhoneValid = isNonEmptyString(phone);

          const isAtcIdValid = isNonEmptyString(atcId);
          const checked = isAtcIdValid ? selectedSet.has(atcId) : false;

          const checkboxDisabled = Boolean(
            disabled ||
              !isAtcIdValid ||
              (!abonent.IsAvailable && !abonent.IsSelected)
          );

          return (
            <NumberRow
              key={abonent.AtcId ?? abonent.PhoneNumber ?? `available-${index}`}
              abonent={abonent}
              checked={checked}
              disabled={checkboxDisabled || !isPhoneValid}
              onChange={(next) => {
                if (!isAtcIdValid) return;
                if (!isPhoneValid) return;
                if (checkboxDisabled) return;

                setAtcIdChecked(atcId, next);
              }}
              showRecordingWarning={
                isSelectableByBusinessRules(abonent) &&
                abonent.IsRecordingOn === false
              }
              showUsedAtInfo={false}
            />
          );
        })}
      </div>

      <Text strong>
        Номера абонентов выбранные в других филиалах организации нельзя выбрать
        в текущем активном филиале.
      </Text>

      <div className={`${PF}__list`}>
        {unavailableAbonents.map((abonent, index) => (
          <NumberRow
            key={abonent.AtcId ?? abonent.PhoneNumber ?? `unavailable-${index}`}
            abonent={abonent}
            checked={false}
            disabled
            onChange={() => undefined}
            showRecordingWarning={false}
            showUsedAtInfo
          />
        ))}
      </div>
    </div>
  );
};
